import React, { createContext, SetStateAction, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { SortingState } from '@tanstack/react-table';
import { usePost } from '../API';
import { ReportFilterConfig } from '@aidkitorg/types/lib/admin-panel/filters';
import { logAdminPanelError } from '../utils/adminPanel.utils';
import { ReportConfig } from '@aidkitorg/aidkit/lib/admin_panel/reports/domain/ReportConfig';

// Cache types
type CacheKey = string;
type CacheEntry = {
  data: ReportData;
  timestamp: number;
  ttl: number;
};
type ReportDataCache = Map<CacheKey, CacheEntry>;

const CACHE_MAX_ENTRIES = 20;
const CACHE_TTL_MS = 30000;

export type Report = {
  id: string;
  title: string;
  description: string;
  created_by: string;
  created_by_name: string;
  created_at: number;
  updated_at: number;
  view_only: boolean;
  config?: ReportConfig;
};

export type ReportWithConfig = Report & {
  config: ReportConfig;
};

export type ReportDataRow = Record<string, any>;

export type ReportData = {
  data: ReportDataRow[];
  columns: { key: string; label: string }[];
  total: number;
  lastUpdated: number;
};

export type CreateReportParams = {
  title: string;
  description: string;
  config: ReportConfig;
};

export type UpdateReportParams = {
  id: string;
  title?: string;
  description?: string;
  config?: ReportConfig;
};

export type GenerateReportDataParams = {
  config: ReportConfig;
  page?: number;
  limit?: number;
  search?: string;
  sorting?: SortingState;
  viewFilters?: ReportFilterConfig;
  debounceMs?: number;
};

export type GenerateReportDataImmediateParams = {
  config: ReportConfig;
  page?: number;
  limit?: number;
  search?: string;
  sorting?: SortingState;
  viewFilters?: ReportFilterConfig;
  forceRefresh?: boolean;
};

type ReportsContextType = {
  reports: Array<Report>;
  getReports: (page?: number, limit?: number, search?: string, sorting?: SortingState) => Promise<void>;
  isReportsLoading: boolean;
  isReportsSearching: boolean;
  reportsTotal: number;
  reportsPage: number;
  reportsLimit: number;
  reportsTotalPages: number;
  reportsSearch: string;
  reportsSearchDisplay: string;
  reportsSorting: SortingState;
  
  // Pagination callbacks for reports list
  onReportsPaginationChange: (pagination: { pageIndex: number; pageSize: number }) => void;
  onReportsSearchChange: (search: string) => void;
  onReportsSortingChange: React.Dispatch<React.SetStateAction<SortingState>>;

  fetchReportById: (id: string) => void;
  getReportById: (id: string) => ReportWithConfig | void;
  reportsById: ReportsById;

  deleteReportById: (id: string) => void;
  
  createReport: (params: CreateReportParams) => Promise<string | void>;
  updateReport: (params: UpdateReportParams) => Promise<void>;

  generateReportData: (params: GenerateReportDataParams) => Promise<ReportData | void>;
  generateReportDataImmediate: (params: GenerateReportDataImmediateParams) => Promise<ReportData | void>;
  
  // Report builder state
  stagedConfig: ReportConfig | null;
  setStagedConfig: React.Dispatch<SetStateAction<ReportConfig | null>>;
};

type ReportsById = Record<string, ReportWithConfig>;

type ReportsProviderProps = {
  children: React.ReactNode;
};

const ReportsContext = createContext<ReportsContextType>({
  reports: [],
  getReports: async () => {},
  isReportsLoading: false,
  isReportsSearching: false,
  reportsTotal: 0,
  reportsPage: 1,
  reportsLimit: 25,
  reportsTotalPages: 0,
  reportsSearch: '',
  reportsSearchDisplay: '',
  reportsSorting: [],
  
  onReportsPaginationChange: () => {},
  onReportsSearchChange: () => {},
  onReportsSortingChange: () => {},

  fetchReportById: async () => {},
  getReportById: () => {},
  reportsById: {},

  deleteReportById: async () => {},

  createReport: async (_params) => {},
  updateReport: async (_params) => {},

  generateReportData: async (_params) => {},
  generateReportDataImmediate: async (_params) => {},

  stagedConfig: null,
  setStagedConfig: (_prev) => {},
});

export const useReports = () => {
  const context = useContext(ReportsContext);
  if (!context) {
    throw new Error("useReports must be used within a ReportsProvider");
  }

  return context;
};

export const ReportsProvider: React.FC<ReportsProviderProps> = ({ children }) => {
  const [reports, setReports] = useState<Array<Report>>([]);
  const [isReportsLoading, setIsReportsLoading] = useState(false);
  const [isReportsSearching, setIsReportsSearching] = useState(false);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [reportsPage, setReportsPage] = useState(1);
  const [reportsLimit, setReportsLimit] = useState(25);
  const [reportsTotalPages, setReportsTotalPages] = useState(0);
  const [reportsSearch, setReportsSearch] = useState(''); // For API calls
  const [reportsSearchDisplay, setReportsSearchDisplay] = useState(''); // For UI display
  const [reportsSorting, setReportsSorting] = useState<SortingState>([]);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generateDataTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Report data cache
  const reportDataCacheRef = useRef<ReportDataCache>(new Map());

  const [stagedConfig, setStagedConfig] = useState<ReportConfig | null>(null);
  const [reportsById, setReportsById] = useState<ReportsById>({});

  // Hash function for cache keys based on the report config
  const hashString = useCallback((str: string): string => {
    let hash = 0;
    if (str.length === 0) return hash.toString();
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }, []);

  // Cache utility functions
  const generateCacheKey = useCallback((params: {
    config: ReportConfig;
    page?: number;
    limit?: number;
    search?: string;
    sorting?: SortingState;
    viewFilters?: ReportFilterConfig;
  }) => {
    const keyData = JSON.stringify({
      config: params.config,
      page: params.page || 1,
      limit: params.limit || 50,
      search: params.search || '',
      sorting: params.sorting || [],
      viewFilters: params.viewFilters || null
    });
    return hashString(keyData);
  }, [hashString]);

  const getCachedData = useCallback((cacheKey: string): ReportData | null => {
    const cache = reportDataCacheRef.current;
    const entry = cache.get(cacheKey);
    
    if (!entry) return null;
    
    const now = Date.now();
    if (now > entry.timestamp + entry.ttl) {
      cache.delete(cacheKey);
      return null;
    }
    
    return entry.data;
  }, []);

  const setCachedData = useCallback((cacheKey: string, data: ReportData, ttlMs: number = CACHE_TTL_MS) => {
    const cache = reportDataCacheRef.current;
    
    // Lazy cleanup: remove expired entries when cache gets too big
    if (cache.size >= CACHE_MAX_ENTRIES) {
      const now = Date.now();
      const entries = Array.from(cache.entries());
      
      // Remove expired entries
      for (const [key, entry] of entries) {
        if (now > entry.timestamp + entry.ttl) {
          cache.delete(key);
        }
      }
      
      // If still too big after cleanup, remove oldest entry
      if (cache.size >= CACHE_MAX_ENTRIES) {
        const remainingEntries = Array.from(cache.entries());
        remainingEntries.sort((a, b) => a[1].timestamp - b[1].timestamp);
        cache.delete(remainingEntries[0][0]);
      }
    }
    
    cache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      ttl: ttlMs
    });
  }, []);

  // Cleanup timeouts and empty cache on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (generateDataTimeoutRef.current) {
        clearTimeout(generateDataTimeoutRef.current);
      }
      reportDataCacheRef.current.clear();
    };
  }, []);

  const getAdminPanelReports = usePost("/api/admin_panel/reports");
  const fetchReportByIdPost = usePost("/api/admin_panel/report");
  const createReportPost = usePost("/api/admin_panel/reports/create");
  const updateReportPost = usePost("/api/admin_panel/reports/update");
  const deleteReportPost = usePost("/api/admin_panel/reports/delete");
  const generateReportDataPost = usePost("/api/admin_panel/reports/generate");

  const getReports = async (page = 1, limit = 25, search?: string, sorting?: SortingState) => {
    const searchParam = search !== undefined ? search : reportsSearch;
    const sortingParam = sorting !== undefined ? sorting : reportsSorting;
    
    // Set appropriate loading state
    const isSearchOperation = searchParam !== reportsSearch;
    if (isSearchOperation) {
      setIsReportsSearching(true);
    } else {
      setIsReportsLoading(true);
    }
    
    try {
      const params: any = { page, limit };
      
      // Only add search param if it's not empty
      if (searchParam && searchParam.trim()) {
        params.search = searchParam.trim();
      }
      
      // Add sorting params if they exist
      if (sortingParam && sortingParam.length > 0) {
        params.sorting = sortingParam.map(sort => ({
          sortBy: sort.id,
          sortOrder: sort.desc ? 'desc' as const : 'asc' as const
        }));
      }
      
      const result = await getAdminPanelReports(params);
      
      if (result.success && result.data) {
        const newData = {
          reports: result.data.reports.data,
          total: result.data.reports.total,
          totalPages: result.data.reports.totalPages,
          timestamp: Date.now()
        };
        
        // Update state
        setReports(newData.reports);
        setReportsTotal(newData.total);
        setReportsPage(result.data.reports.page);
        setReportsLimit(result.data.reports.limit);
        setReportsTotalPages(newData.totalPages);
        
        // Update search state if search param was provided
        if (search !== undefined) {
          setReportsSearch(search);
        }
      } else if (result.error) {
        logAdminPanelError(result.error, result.message);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setIsReportsLoading(false);
      setIsReportsSearching(false);
    }
  };

  // Use ref to avoid circular dependencies
  const getReportsRef = useRef(getReports);
  getReportsRef.current = getReports;

  const onReportsPaginationChange = useCallback((pagination: { pageIndex: number; pageSize: number }) => {
    // Convert 0-based pageIndex to 1-based page for API
    const page = pagination.pageIndex + 1;
    const limit = pagination.pageSize;
    
    // If page size changed, always go to page 1
    if (limit !== reportsLimit) {
      getReportsRef.current(1, limit, reportsSearch, reportsSorting);
    } else {
      getReportsRef.current(page, limit, reportsSearch, reportsSorting);
    }
  }, [reportsLimit, reportsSearch, reportsSorting]);

  const onReportsSearchChange = useCallback((search: string) => {
    // Clear existing timeout first
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Update display state immediately for UI feedback
    setReportsSearchDisplay(search);
    
    const trimmedSearch = search.trim();
    
    // If search is empty, call API immediately (clearing search)
    if (!trimmedSearch) {
      setIsReportsSearching(false);
      setReportsSearch(search);
      getReportsRef.current(1, reportsLimit, search, reportsSorting);
      return;
    }
    
    // For non-empty search, show searching state and debounce the API call
    setIsReportsSearching(true);
    
    const timeoutId = setTimeout(() => {
      setReportsSearch(search); // Update API state after debounce
      getReportsRef.current(1, reportsLimit, search, reportsSorting);
    }, 400);
    
    searchTimeoutRef.current = timeoutId;
  }, [reportsLimit, reportsSorting]);

  const onReportsSortingChange = useCallback((updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
    const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(reportsSorting) : updaterOrValue;
    setReportsSorting(newSorting);
    // Fetch data with new sorting, reset to first page
    getReportsRef.current(1, reportsLimit, reportsSearch, newSorting);
  }, [reportsSorting, reportsLimit, reportsSearch]);

  const fetchReportById = async (id: string) => {
    const result = await fetchReportByIdPost({ reportId: id });

    if (result.success && result.data) {
      const report = result.data;
      const reportWithConfig: ReportWithConfig = {
        id: report.id,
        title: report.title,
        description: report.description,
        created_by: report.created_by,
        created_by_name: report.created_by_name,
        created_at: report.created_at,
        updated_at: report.updated_at,
        view_only: false, // Will be determined by service
        config: report.config
      };
      
      setReportsById((prev) => ({
        ...prev,
        [report.id]: reportWithConfig
      }));
    } else if (result.error) {
      logAdminPanelError(result.error, result.message);
      throw new Error(result.message || 'Failed to fetch report');
    } else {
      throw new Error('Failed to fetch report - unknown error');
    }
  };

  const getReportById = (id: string) => {
    return reportsById[id];
  };

  const deleteReportById = async (id: string) => {
    const result = await deleteReportPost({ reportId: id });

    if (result.success) {
      // Remove from local state
      setReportsById(prev => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
      
      // Refresh the current page to get updated data and totals
      await getReports(reportsPage, reportsLimit, reportsSearch, reportsSorting);
    } else if (result.error) {
      logAdminPanelError(result.error, result.message);
      throw new Error(result.message || 'Failed to delete report');
    }
  };

  const createReport = async ({ title, description, config }: CreateReportParams) => {
    const result = await createReportPost({
      title,
      description,
      config
    });

    if (result.success && result.data) {
      return result.data.id;
    } else if (result.error) {
      logAdminPanelError(result.error, result.message);
    }
  };

  const updateReport = async ({ id, title, description, config }: UpdateReportParams) => {
    const updateData: any = { reportId: id };
    
    // Always include all parameters, but let backend decide what to update
    updateData.title = title;
    updateData.description = description;
    updateData.config = config;

    const result = await updateReportPost(updateData);

    if (result.success) {
      // Refresh the report data
      await fetchReportById(id);
    } else if (result.error) {
      logAdminPanelError(result.error, result.message);
      throw new Error(result.message || `Failed to update report ${id}`);
    } else {
      throw new Error(`Failed to update report ${id} - unknown error`);
    }
  };

  // Immediate version of generateReportData (no debouncing)
  const generateReportDataImmediate = useCallback(async ({ config, page = 1, limit = 50, search, sorting, viewFilters, forceRefresh = false }: GenerateReportDataImmediateParams): Promise<ReportData | void> => {    
    const params: any = { config, page, limit };
    if (search && search.trim()) {
      params.search = search.trim();
    }
    
    // Add viewFilters if provided
    if (viewFilters) {
      params.viewFilters = viewFilters;
    }
    
    // Add forceRefresh parameter
    if (forceRefresh) {
      params.forceRefresh = forceRefresh;
    }

    // Add sorting parameters if provided
    if (sorting && sorting.length > 0) {
      params.sorting = sorting.map(sort => ({
        sortBy: sort.id,
        sortOrder: sort.desc ? 'desc' as const : 'asc' as const
      }));
    }
    
    try {
      const result = await generateReportDataPost(params);
      
      if (result.success && result.data) {
        const reportData = {
          data: result.data.data,
          columns: result.data.columns,
          total: result.data.total,
          lastUpdated: result.data.lastUpdated
        };
        // Only cache if this is not a force refresh
        if (!forceRefresh) {
          const cacheKey = generateCacheKey({ config, page, limit, search, sorting, viewFilters });
          setCachedData(cacheKey, reportData, CACHE_TTL_MS);
        }
        
        return reportData;
      } else if (result.error) {
        logAdminPanelError(result.error, result.message);
      }
    } catch (error) {
      throw error;
    }
  }, [generateReportDataPost, generateCacheKey, setCachedData]);
  
  // Debounced version of generateReportData
  const generateReportData = useCallback(async ({ config, page = 1, limit = 50, search, sorting, viewFilters, debounceMs = 800 }: GenerateReportDataParams): Promise<ReportData | void> => {
    // Check cache first - return immediately if data exists
    const cacheKey = generateCacheKey({ config, page, limit, search, sorting, viewFilters });
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      return cachedData;
    }
    
    return new Promise((resolve, reject) => {
      // Clear any existing timeout
      if (generateDataTimeoutRef.current) {
        clearTimeout(generateDataTimeoutRef.current);
      }
      
      // Set up new timeout
      generateDataTimeoutRef.current = setTimeout(async () => {
        try {
          const result = await generateReportDataImmediate({ config, page, limit, search, sorting, viewFilters });
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, debounceMs);
    });
  }, [generateCacheKey, getCachedData, generateReportDataImmediate]);

  const value = {
    reports,
    getReports,
    isReportsLoading,
    isReportsSearching,
    reportsTotal,
    reportsPage,
    reportsLimit,
    reportsTotalPages,
    reportsSearch,
    reportsSearchDisplay,
    reportsSorting,
    
    onReportsPaginationChange,
    onReportsSearchChange,
    onReportsSortingChange,

    fetchReportById,
    getReportById,
    reportsById,
    deleteReportById,

    createReport,
    updateReport,

    generateReportData,
    generateReportDataImmediate,

    stagedConfig,
    setStagedConfig,
  };

  return (
    <ReportsContext.Provider value={value}>
      {children}
    </ReportsContext.Provider>
  );
};